/**
 * Shared RTK Query API layer using generated OpenAPI types.
 *
 * Regenerate the schema files with `npm run generate:types` in
 * `packages/report-app` after updating the OpenAPI specs.
 */

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"
import type { components, operations } from "./generated/mm-report.schema"

// Type aliases for schemas
export type EventUserAttendanceSummary =
  components["schemas"]["EventUserAttendanceSummary"]
export type EventReportInfo = components["schemas"]["EventSummaryInfo"]
export type UserEventRsvpReport = components["schemas"]["UserEventRsvpSummary"]
export type UserEventRsvpItem = components["schemas"]["UserEventRsvpItem"]
export type UserEventAttendanceReport =
  components["schemas"]["UserEventAttendanceSummary"]
export type OrganizationReportInfo =
  components["schemas"]["OrganizationSummaryInfo"]
export type UserEventAttendanceItem =
  components["schemas"]["UserEventAttendanceItem"]
export type UserEventCreditTypeReport =
  components["schemas"]["UserEventCreditTypeSummary"]
export type UserMandatedTrainingReport =
  components["schemas"]["UserMandatedTrainingSummary"]
export type MandatedTrainingReportInfo =
  components["schemas"]["MandatedTrainingSummaryInfo"]
export type UserReportInfo = components["schemas"]["UserSummary"]
export type CourseSummary = components["schemas"]["CourseSummary"]
export type EventSummary = components["schemas"]["EventSummary"]
export type LearningPathReport = components["schemas"]["LearningPathSummary"]
export type LearningPathSummary = components["schemas"]["LearningPathSummary"]
export type OrganizationSummary = components["schemas"]["OrganizationSummary"]
export type UserMembershipSummary =
  components["schemas"]["UserMembershipSummary"]
export type LearningPathInfo = components["schemas"]["LearningPathInfo"]
export type UserInfo = components["schemas"]["UserInfo"]
export type ResponseMetadata = components["schemas"]["ResponseMetadata"]
export type Error = components["schemas"]["Error"]

export type UserCourseSummary = components["schemas"]["UserCourseSummary"]

// Operation types
export type GetUserCourseParams =
  operations["getUserCourseReport"]["parameters"]["query"]
export type GetUserCourseResponse =
  operations["getUserCourseReport"]["responses"]["200"]["content"]["application/json"]
export type GetCourseParams =
  operations["getCourseReport"]["parameters"]["query"]
export type GetCourseResponse =
  operations["getCourseReport"]["responses"]["200"]["content"]["application/json"]
export type GetEventParams = operations["getEventReport"]["parameters"]["query"]
export type GetEventResponse =
  operations["getEventReport"]["responses"]["200"]["content"]["application/json"]
export type GetLearningPathParams =
  operations["getLearningPathReport"]["parameters"]["query"]
export type GetLearningPathResponse =
  operations["getLearningPathReport"]["responses"]["200"]["content"]["application/json"]
export type GetOrganizationParams =
  operations["getOrganizationReport"]["parameters"]["path"]
export type GetOrganizationResponse =
  operations["getOrganizationReport"]["responses"]["200"]["content"]["application/json"]
export type GetUserParams = operations["getUserReport"]["parameters"]["query"]
export type GetUserResponse =
  operations["getUserReport"]["responses"]["200"]["content"]["application/json"]
export type GetUserLearningPathParams =
  operations["getUserLearningPathReport"]["parameters"]["query"]
export type GetUserLearningPathResponse =
  operations["getUserLearningPathReport"]["responses"]["200"]["content"]["application/json"]
export type GetUserEventCreditTypeParams =
  operations["getUserEventCreditTypeReport"]["parameters"]["query"]
export type GetUserEventCreditTypeResponse =
  operations["getUserEventCreditTypeReport"]["responses"]["200"]["content"]["application/json"]
export type GetUserEventAttendanceParams =
  operations["getUserEventAttendanceReport"]["parameters"]["query"]
export type GetUserEventAttendanceResponse =
  operations["getUserEventAttendanceReport"]["responses"]["200"]["content"]["application/json"]
export type GetUserEventRsvpParams =
  operations["getUserEventRsvpReport"]["parameters"]["query"]
export type GetUserEventRsvpResponse =
  operations["getUserEventRsvpReport"]["responses"]["200"]["content"]["application/json"]
export type GetEventUserAttendanceSummaryParams =
  operations["getEventUserAttendanceSummary"]["parameters"]["query"]
export type GetEventUserAttendanceSummaryResponse =
  operations["getEventUserAttendanceSummary"]["responses"]["200"]["content"]["application/json"]
export type GetUserMandatedTrainingParams =
  operations["getUserMandatedTrainingReport"]["parameters"]["query"]
export type GetUserMandatedTrainingResponse =
  operations["getUserMandatedTrainingReport"]["responses"]["200"]["content"]["application/json"]

type UserLearningPathCsvParams = Pick<
  GetUserLearningPathParams,
  | "org_id"
  | "group"
  | "created_start"
  | "created_end"
  | "updated_start"
  | "updated_end"
  | "status"
>
type UserEventCreditTypeCsvParams = Pick<
  GetUserEventCreditTypeParams,
  "org_id" | "group" | "start_date" | "end_date"
>
type UserEventAttendanceCsvParams = Pick<
  GetUserEventAttendanceParams,
  "org_id" | "group" | "start_date" | "end_date"
>
type UserEventRsvpCsvParams = Pick<
  GetUserEventRsvpParams,
  "org_id" | "group" | "start_date" | "end_date"
>
type EventUserAttendanceSummaryCsvParams = Pick<
  GetEventUserAttendanceSummaryParams,
  "org_id" | "group" | "start_date" | "end_date"
>
type UserMandatedTrainingCsvParams = Pick<
  GetUserMandatedTrainingParams,
  "org_id" | "group" | "start_date" | "end_date"
>
type CourseCsvParams = Pick<
  GetCourseParams,
  | "org_id"
  | "group"
  | "created_start"
  | "created_end"
  | "updated_start"
  | "updated_end"
>
type EventCsvParams = Pick<
  GetEventParams,
  "org_id" | "group" | "start_date" | "end_date"
>
type LearningPathCsvParams = Pick<
  GetLearningPathParams,
  | "org_id"
  | "group"
  | "created_start"
  | "created_end"
  | "updated_start"
  | "updated_end"
  | "status"
>
type UserCsvParams = Pick<GetUserParams, "org_id" | "group">

const apiUrl = import.meta.env.VITE_API_URL || ""

let csrfTokenCache: { token: string; timestamp: number } | null = null
const CSRF_TOKEN_TTL = 30 * 60 * 1000

const getCsrfToken = async (): Promise<string> => {
  if (
    csrfTokenCache &&
    Date.now() - csrfTokenCache.timestamp < CSRF_TOKEN_TTL
  ) {
    return csrfTokenCache.token
  }

  const response = await fetch(`${apiUrl}/session/token`, {
    credentials: "include",
  })

  if (!response.ok) {
    throw new Error("Unable to fetch CSRF token")
  }

  const token = await response.text()
  csrfTokenCache = { token, timestamp: Date.now() }
  return token
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: `${apiUrl}/api`,
  credentials: "include",
  jsonContentType: "application/json",
  prepareHeaders: async (headers, api) => {
    if (!headers.has("Accept")) {
      headers.set("Accept", "application/json")
    }
    // Only fetch CSRF token for mutations
    if (api.type === "mutation") {
      const token = await getCsrfToken()
      headers.set("X-CSRF-Token", token)
    }
    return headers
  },
})

export const reportApi = createApi({
  reducerPath: "reportApi",
  baseQuery: rawBaseQuery,
  tagTypes: [
    "ReportCourse",
    "ReportEvent",
    "ReportLearningPath",
    "ReportOrganization",
    "ReportUser",
    "ReportUserLearningPath",
    "ReportUserCourse",
    "ReportUserEventCreditType",
    "ReportUserEventAttendance",
    "ReportUserEventRsvp",
    "ReportEventUserAttendanceSummary",
    "ReportUserMandatedTraining",
    "OrgServiceAccounts",
    "ServiceAccountApiKeys",
  ],
  endpoints: (builder) => ({
    getCourses: builder.query<GetCourseResponse, GetCourseParams>({
      query: (params) => ({
        url: "/report/course",
        params,
      }),
      providesTags: ["ReportCourse"],
    }),
    getEvents: builder.query<GetEventResponse, GetEventParams>({
      query: (params) => ({
        url: "/report/event",
        params,
      }),
      providesTags: ["ReportEvent"],
    }),
    getLearningPaths: builder.query<
      GetLearningPathResponse,
      GetLearningPathParams
    >({
      query: (params) => ({
        url: "/report/learning-path",
        params,
      }),
      providesTags: ["ReportLearningPath"],
    }),
    getOrganization: builder.query<
      GetOrganizationResponse,
      GetOrganizationParams
    >({
      query: ({ org_id }) => ({
        url: `/report/organization/${org_id}`,
      }),
      providesTags: ["ReportOrganization"],
    }),
    getUsers: builder.query<GetUserResponse, GetUserParams>({
      query: (params) => ({
        url: "/report/user",
        params,
      }),
      providesTags: ["ReportUser"],
    }),
    getUserCourses: builder.query<GetUserCourseResponse, GetUserCourseParams>({
      query: (params) => ({ url: "/report/user-course", params }),
      providesTags: ["ReportUserCourse"],
    }),
    getUserLearningPath: builder.query<
      GetUserLearningPathResponse,
      GetUserLearningPathParams
    >({
      query: (params) => ({
        url: "/report/user-learning-path",
        params,
      }),
      providesTags: ["ReportUserLearningPath"],
    }),
    getUserEventCreditType: builder.query<
      GetUserEventCreditTypeResponse,
      GetUserEventCreditTypeParams
    >({
      query: (params) => ({
        url: "/report/user-event-credit-type",
        params,
      }),
      providesTags: ["ReportUserEventCreditType"],
    }),
    getUserEventAttendance: builder.query<
      GetUserEventAttendanceResponse,
      GetUserEventAttendanceParams
    >({
      query: (params) => ({
        url: "/report/user-event-attendance",
        params,
      }),
      providesTags: ["ReportUserEventAttendance"],
    }),
    getUserEventRsvp: builder.query<
      GetUserEventRsvpResponse,
      GetUserEventRsvpParams
    >({
      query: (params) => ({
        url: "/report/user-event-rsvp",
        params,
      }),
      providesTags: ["ReportUserEventRsvp"],
    }),
    getEventUserAttendanceSummary: builder.query<
      GetEventUserAttendanceSummaryResponse,
      GetEventUserAttendanceSummaryParams
    >({
      query: (params) => ({
        url: "/report/event-user-attendance-summary",
        params,
      }),
      providesTags: ["ReportEventUserAttendanceSummary"],
    }),
    getUserMandatedTraining: builder.query<
      GetUserMandatedTrainingResponse,
      GetUserMandatedTrainingParams
    >({
      query: (params) => ({
        url: "/report/user-mandated-training",
        params,
      }),
      providesTags: ["ReportUserMandatedTraining"],
    }),
  }),
})

// Export hooks
export const {
  useGetCoursesQuery,
  useGetEventsQuery,
  useGetLearningPathsQuery,
  useGetOrganizationQuery,
  useGetUsersQuery,
  useGetUserLearningPathQuery,
  useGetUserCoursesQuery,
  useGetUserEventCreditTypeQuery,
  useGetUserEventAttendanceQuery,
  useGetUserEventRsvpQuery,
  useGetEventUserAttendanceSummaryQuery,
  useGetUserMandatedTrainingQuery,
} = reportApi

const CSV_PAGINATION_PARAMS = new Set(["limit", "offset", "page", "page_size"])

const buildCsvQueryParams = (params: Record<string, unknown>) => {
  const queryParams = new URLSearchParams()
  Object.entries(params)
    .filter(
      ([key, value]) =>
        !CSV_PAGINATION_PARAMS.has(key) &&
        value !== undefined &&
        value !== null &&
        value !== ""
    )
    .forEach(([key, value]) => {
      queryParams.append(key, String(value))
    })
  return queryParams
}

const downloadCsvReport = async (
  path: string,
  fileName: string,
  params: Record<string, unknown>
) => {
  const apiUrl = import.meta.env.VITE_API_URL || ""
  const queryParams = buildCsvQueryParams(params)
  const response = await fetch(
    `${apiUrl}/api${path}?${queryParams.toString()}`,
    {
      credentials: "include",
      headers: {
        Accept: "text/csv",
      },
    }
  )

  if (!response.ok) {
    throw new Error(`CSV export failed with status ${response.status}`)
  }

  const blob = await response.blob()
  const downloadUrl = URL.createObjectURL(blob)
  const link = document.createElement("a")

  link.href = downloadUrl
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(downloadUrl)
}

// CSV Export function for getCourse
export const exportCoursesAsCSV = (params: CourseCsvParams) => {
  return downloadCsvReport(
    "/report/course",
    `Courses-${new Date().toISOString().split("T")[0]}.csv`,
    params
  )
}

// CSV Export function for getEvent
export const exportEventsAsCSV = (params: EventCsvParams) => {
  return downloadCsvReport(
    "/report/event",
    `Events-${new Date().toISOString().split("T")[0]}.csv`,
    params
  )
}

// CSV Export function for getLearningPath
export const exportLearningPathsAsCSV = (params: LearningPathCsvParams) => {
  return downloadCsvReport(
    "/report/learning-path",
    `LearningPaths-${new Date().toISOString().split("T")[0]}.csv`,
    params
  )
}

// CSV Export function for getUser
export const exportUsersAsCSV = (params: UserCsvParams) => {
  return downloadCsvReport(
    "/report/user",
    `Users-${new Date().toISOString().split("T")[0]}.csv`,
    params
  )
}

// CSV Export function for getUserLearningPath
export const exportUserLearningPathAsCSV = (
  params: UserLearningPathCsvParams
) => {
  return downloadCsvReport(
    "/report/user-learning-path",
    `UserLearningPath-${new Date().toISOString().split("T")[0]}.csv`,
    params
  )
}

// CSV Export function for getUserEventCreditType
export const exportUserEventCreditTypeAsCSV = (
  params: UserEventCreditTypeCsvParams
) => {
  return downloadCsvReport(
    "/report/user-event-credit-type",
    `UserEventCreditType-${new Date().toISOString().split("T")[0]}.csv`,
    params
  )
}

// CSV Export function for getUserEventAttendance
export const exportUserEventAttendanceAsCSV = (
  params: UserEventAttendanceCsvParams
) => {
  return downloadCsvReport(
    "/report/user-event-attendance",
    `UserEventAttendance-${new Date().toISOString().split("T")[0]}.csv`,
    params
  )
}

// CSV Export function for getUserEventRsvp
export const exportUserEventRsvpAsCSV = (params: UserEventRsvpCsvParams) => {
  return downloadCsvReport(
    "/report/user-event-rsvp",
    `UserEventRsvp-${new Date().toISOString().split("T")[0]}.csv`,
    params
  )
}

// CSV Export function for getEventUserAttendanceSummary
export const exportEventUserAttendanceSummaryAsCSV = (
  params: EventUserAttendanceSummaryCsvParams
) => {
  return downloadCsvReport(
    "/report/event-user-attendance-summary",
    `EventUserAttendanceSummary-${new Date().toISOString().split("T")[0]}.csv`,
    params
  )
}

// CSV Export function for getUserMandatedTraining
export const exportUserMandatedTrainingAsCSV = (
  params: UserMandatedTrainingCsvParams
) => {
  return downloadCsvReport(
    "/report/user-mandated-training",
    `UserMandatedTraining-${new Date().toISOString().split("T")[0]}.csv`,
    params
  )
}

// Utility function for extracting pagination metadata
// Handles both 'meta' and 'metadata' field names for backwards compatibility
export function getPaginationMeta<
  T extends { meta?: unknown; metadata?: unknown }
>(response?: T) {
  return (response?.metadata ?? response?.meta) as
    | {
        total: number
        limit: number
        offset: number
        count: number
      }
    | undefined
}

// CSV Export function for getUserCourse
export const exportUserCoursesAsCSV = (
  params: Omit<GetUserCourseParams, "limit" | "offset">
) => {
  return downloadCsvReport(
    "/report/user-course",
    `UserCourses-${new Date().toISOString().split("T")[0]}.csv`,
    params
  )
}
